import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { useCallback, useState } from "react";
import { Link } from "react-router";

import { TOP_BANNER_HEIGHT, useTopBanner } from "@/app/components/TopBanner";
import { IcoDoubleArrowSelect } from "@/common/components/Icons";
import { NewApp } from "@/common/components/NewApp";
import OrganizationSwitcher from "@/common/components/OrganizationSwitcher";
import OrgLogo from "@/common/components/OrgLogo";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { EnvironmentUrl } from "@reflag/shared/urls";

export function AppSwitcher() {
  const { org, app: currentApp } = useEnvironment();

  const [isNewAppOpen, setIsNewAppOpen] = useState(false);

  const onNewAppClose = useCallback(
    () => setIsNewAppOpen(false),
    [setIsNewAppOpen],
  );

  const { showBanner } = useTopBanner();
  const selectedBgColor = useColorModeValue("gray.50", "gray.750");

  const [isOrgSwitcherOpen, setIsOrgSwitcherOpen] = useState(false);

  return (
    <>
      <Menu
        autoSelect={false}
        placement="bottom-start"
        size="md"
        closeOnSelect
        matchWidth
      >
        <MenuButton
          _hover={{ bg: useColorModeValue("gray.100", "gray.800") }}
          as={Button}
          bg="transparent"
          borderRadius="md"
          flexShrink={0}
          mb={-1}
          mt={2.5}
          px={2}
          py={6}
          rightIcon={
            <Box color="dimmed" ml={-2} mr={-2}>
              <IcoDoubleArrowSelect size="2xl" />
            </Box>
          }
          size="lg"
          textAlign="left"
          variant="ghost"
          w="auto"
        >
          <Flex align="center" as="span" gap={2.5} w="100%">
            <OrgLogo
              borderRadius="md"
              name={org.name}
              size="xs"
              url={org.logoUrl}
            />
            <Flex as="span" flexDirection="column" gap={1} overflow="hidden">
              <Text as="span" fontSize="md" fontWeight="medium" isTruncated>
                {currentApp.name}
              </Text>
            </Flex>
          </Flex>
        </MenuButton>
        <Portal>
          <MenuList
            maxH={
              showBanner
                ? `calc(100vh - var(--chakra-sizes-${TOP_BANNER_HEIGHT}) - var(--chakra-sizes-20))`
                : "calc(100vh - var(--chakra-sizes-20))"
            }
            minW="max-content"
            overflowY="auto"
            zIndex={3}
          >
            {org.apps.map((app) => (
              <MenuItem
                key={app.id}
                as={Link}
                bg={currentApp.id === app.id ? selectedBgColor : "transparent"}
                to={EnvironmentUrl(
                  app.environments.find((e) => e.isProduction)!,
                )}
              >
                <HStack as="span">
                  <Text as="span">{app.name}</Text>
                  {app.demo ? (
                    <Badge colorScheme="brand" size="xs" variant="subtle">
                      <Text as="span">Demo</Text>
                    </Badge>
                  ) : null}
                </HStack>
              </MenuItem>
            ))}
            <Box bg="popoverBackground" bottom={0} pos="sticky">
              <Button
                m={2}
                size="xs"
                variant="outline"
                onClick={() => setIsNewAppOpen(true)}
              >
                New app
              </Button>
            </Box>
          </MenuList>
        </Portal>
        <NewApp isOpen={isNewAppOpen} onClose={onNewAppClose} />
      </Menu>
      <OrganizationSwitcher
        isOpen={isOrgSwitcherOpen}
        onClose={() => setIsOrgSwitcherOpen(false)}
      />
    </>
  );
}
